import { action, makeObservable, observable, runInAction } from "mobx";
import { fabric } from "fabric";
import fitInBox, { size } from "tools/fitInBox";
import RootStore from "store/rootStore";

import mouseMoveHandler from "./mouseMoveHandler";
import mouseDownHandler from "./mouseDownHandler";
import objectModifiedHandler from "./objectModifiedHandler";

import { ZOOM_MAX, ZOOM_MIN, ENABLE_DOUBLE_CLICK_CLOSE_POLYGON } from "./labelerSegmentationUiStore";
import PolygonDrawer from "./polygonDrawer";

const SEPARATOR = "||";

class FabricSegmentationStore {
  @observable canvas!: fabric.Canvas;
  @observable image!: fabric.Image | null;
  @observable newPoint: any;
  @observable borderModeIndicator: any;

  constructor(private store: RootStore) {
    makeObservable(this);
  }

  bindEvents = () => {
    this.canvas.on("mouse:down", (e: fabric.IEvent) => {
      runInAction(() => {
        mouseDownHandler(e, this.store);
      });
    });
    this.canvas.on("mouse:wheel", (e: fabric.IEvent) => {
      runInAction(() => {
        this.mouseWheelHandler(e);
      });
    });
    this.canvas.on("mouse:move", (e: fabric.IEvent) => {
      runInAction(() => {
        mouseMoveHandler(e, this.store);
      });
    });
    // this.canvas.on("selection:cleared", (e: any) => {
    //     runInAction(() => { this.selectionClearedHandler(e) })
    // });
    // this.canvas.on("selection:created", (e: any) => {
    //     runInAction(() => { this.selectionChangedHandler(e) })
    // });
    // this.canvas.on("selection:updated", (e: any) => {
    //     runInAction(() => { this.selectionChangedHandler(e) })
    // });
    this.canvas.on("object:modified", (modif: any) => {
      runInAction(() => {
        objectModifiedHandler(modif, this.store);
      });
    });

    // this.canvas.on("selection:created", (e: fabric.IEvent) => {
    //     console.log("selection:created ", e)
    // });

    // this.canvas.on("selection:updated", (e: fabric.IEvent) => {
    //     console.log("selection:updated ", e)
    // });

    this.canvas.on("mouse:over", (e: fabric.IEvent) => {
      runInAction(() => {
        this.mouseOverHandler(e);
      });
    });

    this.canvas.on("mouse:dblclick", (e: fabric.IEvent) => {
      runInAction(() => {
        this.mouseDblclickHandler(e);
      });
    });
  };

  bindKeyboardEvents = (e: KeyboardEvent) => {
    console.log("e.key ", e.key);
    switch (e.key) {
      case "Shift":
        runInAction(() => {
          this.shiftHandler(e);
        });
        break;
      case "Escape":
        runInAction(() => {
          this.escapeHandler(e);
        });
        break;
      case "ArrowUp":
        runInAction(() => {
          this.arrowUpHandler(e);
        });
        break;
      case "ArrowDown":
        runInAction(() => {
          this.arrowDownHandler(e);
        });
        break;
      case "b":
        runInAction(() => {
          this.bHandler(e);
        });
      default:
        break;
    }
  };

  /// HANDLER ////////////////////////////
  ////////////////////////////////////////

  shiftHandler = (e: KeyboardEvent) => {
    // if (e.type == "keydown") {
    //     this.moveInZoomHandler(e)
    // }
    this.moveInZoomHandler(e);
  };

  escapeHandler = (e: KeyboardEvent) => {
    if (e.type == "keydown") {
      this.resetZoomHandler();
    }
  };

  arrowUpHandler = (e: KeyboardEvent) => {
    if (e.type == "keydown") {
      this.zoomInHandler();
    }
  };

  arrowDownHandler = (e: KeyboardEvent) => {
    if (e.type == "keydown") {
      this.zoomOutHandler();
    }
  };

  bHandler = (e: KeyboardEvent) => {
    if (
      this.store.labelerSegmentationStore.isReadOnly ||
      this.store.labelerSegmentationUiStore.imageLoadError
    ) {
      return;
    }
    this.borderModeHandler(e);
  };

  borderModeHandler = (e: KeyboardEvent) => {
    const { setBorderMode } = this.store.labelerSegmentationUiStore;

    switch (e.type) {
      case "keydown":
        setBorderMode(true);
        // this.canvas.setCursor("move");
        break;
      case "keyup":
        setBorderMode(false);
        this.removeBorderModeIndicator();
        // this.canvas.setCursor("crosshair");
        break;
      default:
        break;
    }
  };

  resetZoomHandler = () => {
    const { zoom } = this.store.labelerSegmentationUiStore;
    if (zoom !== 1) {
      this.canvas.setViewportTransform([1, 0, 0, 1, 0, 0]);
      // canvas.setZoom(1);
      this.store.labelerSegmentationUiStore.setZoom(1);
    }
  };

  zoomInHandler = () => {
    const { setZoom, zoom } = this.store.labelerSegmentationUiStore;

    if (zoom < ZOOM_MAX) {
      const zoom = this.canvas.getZoom() + 0.1;
      // canvas.setZoom(zoom);
      this.canvas.zoomToPoint(new fabric.Point(0, 0), zoom);
      setZoom(zoom);
    }
  };

  zoomOutHandler = () => {
    const { setZoom, zoom } = this.store.labelerSegmentationUiStore;

    if (zoom > ZOOM_MIN) {
      const zoom = this.canvas.getZoom() - 0.1;
      // canvas.setZoom(zoom);
      this.canvas.zoomToPoint(new fabric.Point(0, 0), zoom);

      setZoom(zoom);
    }
  };

  moveInZoomHandler = (e: KeyboardEvent) => {
    const { setMoveInZoom, isZoomIn } = this.store.labelerSegmentationUiStore;

    if (isZoomIn) {
      switch (e.type) {
        case "keydown":
          setMoveInZoom(true);
          this.canvas.setCursor("move");
          break;
        case "keyup":
          setMoveInZoom(false);
          this.canvas.setCursor("crosshair");
          break;
        default:
          break;
      }
    }
  };

  mouseDblclickHandler = (event: fabric.IEvent) => {
    if (
      !ENABLE_DOUBLE_CLICK_CLOSE_POLYGON ||
      this.store.labelerSegmentationStore.isReadOnly ||
      this.store.labelerSegmentationUiStore.imageLoadError
    ) {
      return;
    }
    this.store.labelerSegmentationUiStore.closeCurrentPolygon();
  };

  mouseOverHandler = (event: fabric.IEvent) => {
    const { imageLoadError } = this.store.labelerSegmentationUiStore;
    this.canvas.setCursor(imageLoadError ? "default" : "crosshair");
    const target = event.target;
    if (target) {
      if (target.stroke === null) {
        // on est sur le fond de l'image
        // this.image.hoverCursor = !selectMode ? "crosshair" : "default";
        if (this.image) {
          this.image.hoverCursor = imageLoadError ? "default" : "crosshair";
        }
        this.canvas.renderAll();
      }
    }
  };

  selectionClearedHandler = (e: any) => {
    // s'il y a le placeholder d'un nouveau en place sur un segment
    // le supprimer
    if (this.newPoint) {
      this.canvas.remove(this.newPoint.object);
      this.setNewPoint(null);
    }

    // this.setState({ polySelectedName: "", labelSelectedIndex: 0 });
    this.canvas.requestRenderAll();
  };

  selectionChangedHandler = (e: any) => {
    const objectSelected = e.selected[0];
    const { tags } = this.store.labelerSegmentationStore;
    const {
      tagSelectedIndex,
      setTagSelectedIndex,
    } = this.store.labelerSegmentationUiStore;

    const label = objectSelected.get("name").split(SEPARATOR)[0];
    if (label !== tags[tagSelectedIndex].legend) {
      setTagSelectedIndex(tags.findIndex((l: any) => l.legend === label));
    }
    // this.setState({ polySelectedName: "", labelSelectedIndex });
    this.canvas.requestRenderAll();
  };

  mouseWheelHandler = (e: fabric.IEvent) => {
    const eventWheel = e.e as WheelEvent;
    const delta = eventWheel.deltaY;
    const currentZoom = this.canvas.getZoom();
    let newZoom = currentZoom + delta / 200;
    // zoom *= 0.999 ** delta;

    if (newZoom <= ZOOM_MIN) {
      newZoom = 1;
      this.canvas.setViewportTransform([1, 0, 0, 1, 0, 0]);
    } else {
      if (newZoom > ZOOM_MAX) newZoom = ZOOM_MAX;
      this.canvas.zoomToPoint(
        new fabric.Point(eventWheel.offsetX, eventWheel.offsetY),
        newZoom
      );
    }
    this.store.labelerSegmentationUiStore.setZoom(newZoom);
    this.canvas.setCursor(newZoom > currentZoom ? "zoom-in" : "zoom-out");
    eventWheel.preventDefault();
    eventWheel.stopPropagation();
  };

  ///////////////////////////////////////////////////////////////

  @action configure = (
    canvasRef: HTMLCanvasElement,
    canvasWrapperRef: any,
    numImage: number
  ) => {
    const {
      dimensions,
      images,
      setNumImage,
    } = this.store.labelerSegmentationStore;

    const {
      calculateScaling,
      loadImage,
    } = this.store.labelerSegmentationUiStore;

    const { width, height } = dimensions;
    const canvas = this.getCanvas(canvasRef);
    setNumImage(numImage);

    //@ts-ignore
    const { offsetHeight, offsetWidth } = canvasWrapperRef;
    const sizeObj: size = fitInBox(
      width,
      height,
      offsetWidth,
      offsetHeight,
      true
    );

    const { height: newHeight, width: newWidth } = sizeObj;
    canvas.setWidth(newWidth);
    canvas.setHeight(newHeight);
    canvas.selection = false;
    canvas.defaultCursor = "pointer"; // "crosshair"

    calculateScaling({ width, height, newWidth, newHeight });
    this.setCanvas(canvas);
    this.bindEvents();

    if (numImage <= images.length) {
      loadImage(numImage);
    }
  };

  getCanvas = (canvasRef: HTMLCanvasElement) => {
    if (this.canvas !== undefined) return this.canvas;
    // enablePointerEvents:false fixes touchpad click detection on Windows
    return new fabric.Canvas(canvasRef, { enablePointerEvents: false } as any);
  };

  /// DRAW POLYGON ///////////////////////

  @action drawPolygon = (poly: any) => {
    PolygonDrawer.draw(poly, this.store);
  };

  ///////////////////////////////////////

  @action addImageToCanvas = (img: any) => {
    const {
      scaling: { x: scaleX, y: scaleY },
      polygons,
    } = this.store.labelerSegmentationUiStore;

    const newImage = new fabric.Image(img, {});
    newImage.set({
      left: 0,
      top: 0,
      selectable: false,
      lockMovementX: false,
      centeredScaling: true,
      lockMovementY: false,
      hoverCursor: "crosshair", // "default","crosshair"
      scaleX,
      scaleY,
      originX: "left",
      originY: "top",
      opacity: 1,
    });

    this.setImage(newImage);

    this.canvas.add(newImage);
    this.canvas.renderAll();

    polygons.forEach((p: any) => this.drawPolygon(p));

    // TODO : imageLoaded: true
  };

  @action removeBorderModeIndicator = () => {
    this.canvas.remove(this.borderModeIndicator);
    this.setBorderModeIndicator(null);
  };

  @action removeImageFromCanvas = () => {
    // @ts-ignore
    this.canvas.remove(this.image);
    this.setImage(null);
  };

  @action toggleAllCanvasVisibility = (visibility: boolean) => {
    this.canvas.getObjects().map((o: fabric.Object) => {
      if (o.type !== "image") {
        o.set("visible", visibility);
      }
    });
    this.canvas.renderAll();
  };

  @action toggleCanvasVisibility = (name: string, visibility: boolean) => {
    this.canvas
      .getObjects()
      .filter((o: fabric.Object) => {
        return o.name && o.name.startsWith(name);
      })
      .map((o: any) => o.set("visible", visibility));

    this.canvas.renderAll();
  };

  @action clearAllCanvasObjects = () =>
    this.canvas
      .getObjects()
      .filter((o: any) => o.get("name"))
      .map((o: any) => this.canvas.remove(o));

  @action clearCanvasObjectsByName = (name: string) =>
    this.canvas
      .getObjects()
      .filter((o: fabric.Object) => o.name && o.name.startsWith(name))
      .map((o: any) => this.canvas.remove(o));

  @action setPolygonsSelectable = () => {
    this.canvas.getObjects().map((o: any) => {
      // on évite de rendre sélectionnable l'image de fond
      // seuls les polygons ont des points
      if (o.get("points")) {
        o.set("selectable", true);
      }
    });
    // this.canvas.setCursor("move");
  };

  @action unsetPolygonsSelectable = () => {
    this.canvas.getObjects().map((o: any) => o.set("selectable", false));
  };

  @action deleteImageFromCanvas = () => {
    //@ts-ignore
    this.canvas.remove(this.image);
    this.setImage(null);
  };

  @action removeNewPoint = () => {
    if (this.newPoint) {
      this.canvas.remove(this.newPoint.object);
      this.setNewPoint(null);
      this.canvas.requestRenderAll();
    }
  };

  /**
 * Test if a point is inside a polygon using ray casting algorithm
 * @param x - X coordinate of the point to test
 * @param y - Y coordinate of the point to test
 * @param points - Array of polygon vertices with x and y coordinates
 * @returns true if the point is inside the polygon, false otherwise
 */
@action isPointInPolygon(x: number, y: number, points: Array<{ x: number; y: number }>): boolean {
  let inside = false;

  for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
    const xi = points[i].x;
    const yi = points[i].y;
    const xj = points[j].x;
    const yj = points[j].y;

    const intersect = (yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

  @action selectPolygonAtPointer = (event: fabric.IEvent) => {
    const pointer = this.canvas.getPointer(event.e);
    const visiblePolygons = this.store.labelerSegmentationUiStore.polygons.filter(
      (polygon: any) => polygon.visible && !polygon.deleted
    );
    let activeObject: any = null;

    // Check polygons in reverse order to pick the top-most one.
    for (let i = visiblePolygons.length - 1; i >= 0; i--) {
      const polygonData = visiblePolygons[i];
      if (this.isPointInPolygon(pointer.x, pointer.y, polygonData.points)) {
        activeObject = this.canvas
          .getObjects()
          .find((obj: any) => obj.name === polygonData.labelValue);
        break;
      }
    }

    if (activeObject) {
      this.canvas.setActiveObject(activeObject);
    } else {
      this.canvas.discardActiveObject();
    }
    this.canvas.requestRenderAll();

    return activeObject;
  };

  //// SETTERS /////////////////////////////////////////////////
  //////////////////////////////////////////////////////////////

  @action setCanvas = (canvas: fabric.Canvas) => {
    this.canvas = canvas;
  };

  @action setNewPoint = (newPoint: any) => {
    this.newPoint = newPoint;
  };

  @action setBorderModeIndicator = (borderModeIndicator: any) => {
    this.borderModeIndicator = borderModeIndicator;
  };

  @action setImage = (image: fabric.Image | null) => {
    this.image = image;
  };
}

export default FabricSegmentationStore;
